import { useEffect, useState, useRef } from 'react';
import ReactSlider from "react-slider";
import { Col, Container, Row, Button, Form } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import useApi from '../utilities/useApi';
import stringUtilities from '../utilities/stringUtilities';
import arrayUtilities from '../utilities/arrayUtilities';
import enums from '../utilities/enums';
import { WorkflowConfigTypesTable } from './common/workflowConfigTypesTable';
import { WorkflowConfigBuilder } from './common/workflowConfigBuilder';
import { ConfigTableRowDragOverlay } from './common/configTableRowDragOverlay';
import ErrorModal from './common/errorModal';
import { DndContext, closestCorners, PointerSensor, useSensors, useSensor, DragOverlay } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Alert } from "react-bootstrap";

export default function EditWorkflowTemplate() {
    const {organisationId, projectId, workflowTemplateId} = useParams();
    const navigate = useNavigate();
    const {api} = useApi();
    const [name, setName] = useState("");
    const [workflowType, setWorkflowType] = useState("9");
    const [hue, setHue] = useState(0);
    const [rgbColor, setRgbColor] = useState(null);
    const [order, setOrder] = useState("0");
    const [hasData, setHasData] = useState(false);
    const [canSave, setCanSave] = useState(false);

    const typesContainerId = "types-droppable-container";
    const typesTwoContainerId = "types2-droppable-container";

    const [configTypes, setConfigTypes] = useState([]);
    const [configTypesTwo, setConfigTypesTwo] = useState([]);
    const [configType, setConfigType] = useState(null);
    const [typeFormData, setTypeFormData] = useState({ "geometryType": enums.GeometryType[0] });
    const [isEditingTypeIndex, setIsEditingTypeIndex] = useState(null);
    const [activeId, setActiveId] = useState(null);
    const [activeContainerId, setActiveContainerId] = useState(typesContainerId);
    const [activeItems, setActiveItems] = useState([]);
    const [errorMessage, setErrorMessage] = useState(null);
    const [icons, setIcons] = useState(null);
    const [isLoadingIcons, setIsLoadingIcons] = useState(false);

    const inputRef = useRef();

    const findContainerItems = (id) => {
        return id === typesContainerId ? configTypes : configTypesTwo;
    };

    const setContainerItems = (id, newItems) => {
        if (id === typesContainerId) {
            setConfigTypes(newItems);
        }
        if (id === typesTwoContainerId) {
            setConfigTypesTwo(newItems);
        }
    };

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }))

    const saveConfig = (type, data) => {
        if (type === "types") {
            updateTypesArray(data, configTypes, setConfigTypes);
        } else if (type === "types2") {
            updateTypesArray(data, configTypesTwo, setConfigTypesTwo);
        }
        setIsEditingTypeIndex(null);
        setConfigType(null);
        setTypeFormData({ "geometryType": enums.GeometryType[0] });
    }

    const updateTypesArray = (data, stateArray, setStateCallback) => {
        if (isEditingTypeIndex !== null) {
            const newArray = stateArray.toSpliced(isEditingTypeIndex, 1, data);
            setStateCallback(newArray);
        } else {
            setStateCallback(prevState => ([...prevState, data]));
        }
    }

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!active || !over) return;
        const activeItemIndex = Number(active.id.split("-")[1]);
        const overItemIndex = Number(over.id.split("-")[1]);
        const overContainerId = over.data.current !== undefined ? over.data.current.sortable.containerId : over.id;
        const overItems = findContainerItems(overContainerId);
        const activeItem = activeItems.find((_, index) => index === activeItemIndex);
        if (activeContainerId === overContainerId) {
            const items = findContainerItems(activeContainerId);
            if (active.id !== over.id) {
                setContainerItems(activeContainerId, arrayMove(items, activeItemIndex, overItemIndex));
            }
        } else {
            const newSourceArray = activeItems.toSpliced(activeItemIndex, 1);
            setContainerItems(activeContainerId, newSourceArray);
            const overIndex = overItems.findIndex((_, index) => index === overItemIndex);
            const newIndex = overIndex === -1 ? overItems.length : overIndex;
            const newOverItems = overItems.toSpliced(newIndex, 0, activeItem);
            setContainerItems(overContainerId, newOverItems);
        }
        setActiveId(null);
        setActiveContainerId(typesContainerId);
        setActiveItems([]);
    }

    const handleChange = () => {
        if (inputRef.current && inputRef.current.files[0]) {
            const inputFile = inputRef.current.files[0];
            if (inputFile.name.toLowerCase().endsWith("json")) {
                if (inputFile) {
                    setErrorMessage(null);
                    const reader = new FileReader();
                    reader.onload = () => {
                        try {
                            const parsedJson = JSON.parse(reader.result);
                            if (typeof parsedJson === 'object' && parsedJson !== null && 'types' in parsedJson && !arrayUtilities.isNullOrEmpty(parsedJson.types)) {
                                setConfigTypes([...parsedJson.types]);
                            }
                            if (typeof parsedJson === 'object' && parsedJson !== null && 'types2' in parsedJson && !arrayUtilities.isNullOrEmpty(parsedJson.types2)) {
                                setConfigTypesTwo([...parsedJson.types2]);
                            }
                        } catch (error) {
                            setErrorMessage(`Error reading JSON file: ${error.message}`);
                        }
                    }
                    reader.readAsText(inputFile);
                }
            } else setErrorMessage("Invalid file extension. File should be of type .json");
        }
    }

    const handleDownload = () => {
        const jsonString = JSON.stringify({ types: configTypes, types2: configTypesTwo }, null, 2);
        const blob = new Blob([jsonString], { type: 'text/plain' });
        const fileURL = URL.createObjectURL(blob);
        const downloadLink = document.createElement('a');
        downloadLink.href = fileURL;
        downloadLink.download = `${stringUtilities.toFriendlyString(name)}.json`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        URL.revokeObjectURL(fileURL);
    }

    const initialiseWorkflowTypeValue = (type) => {
        return (type !== 9 && type !== 10) ? 9 : type
    }

    const fetchWorkflowTemplate = () => {
        api.getWorkflowTemplate(
            organisationId, workflowTemplateId,
            data => {
                setName(data.name);
                setWorkflowType(String(initialiseWorkflowTypeValue(data.workflowTypeId)));
                setHue(data.hue);
                setRgbColor(stringUtilities.checkColorHash(data.color));
                setOrder(data.orderBy);
                setHasData(data.hasData);
                if (data.configuration) {
                    const obj = JSON.parse(data.configuration);
                    setConfigTypes(obj.types ?? []);
                    setConfigTypesTwo(obj.types2 ?? []);
                }
            },
            () => {}
        )
    }

    useEffect(() => {
        fetchWorkflowTemplate();
    }, [])

    useEffect(() => {
        setIsLoadingIcons(true);
        api.getIcons(
            organisationId,
            (data) => { setIcons(data); setIsLoadingIcons(false); },
            () => setIsLoadingIcons(false)
        );
    }, [])

    useEffect(() => setCanSave(!stringUtilities.isNullOrEmpty(name) && hue !== undefined && !stringUtilities.isNullOrEmpty(workflowType)), [workflowType, hue, name])

    const updateTemplate = () => {
        const configuration = JSON.stringify({ types: configTypes, types2: configTypesTwo });
        api.editWorkflowTemplate(
            organisationId, workflowTemplateId, name, parseInt(order), configuration, parseInt(workflowType), parseInt(hue), rgbColor,
            (response) => {
                navigate(`/${ organisationId }/projects/${projectId}/workflows`);
            },
            (error) => {}
        )
    }

    return (
        <Container className='pt-5 pb-lg-4 mt-5 mb-sm-2'>
            <Row>
                <Col className='mb-5'>
                    <div className='d-flex justify-content-between my-5'>
                        <h1 className="h2 m-0">Edit Template</h1>
                    </div>

                    {
                        hasData &&
                        <Alert variant='warning' className='d-flex align-items-center my-5'>
                            <i className='fi-alert-triange me-2 me-sm-3'></i>
                            <p className="m-0">Workflows created from this template have data attached, editing it may have negative consequences!</p>
                        </Alert>
                    }

                    <div>
                        <Form.Group className='mb-3 row align-items-center'>
                            <Form.Label className='form-label fs-sm col-form-label col-md-3' htmlFor="workflow-name">
                                Name
                            </Form.Label>
                            <div className="col-md-9 d-flex align-items-center">
                                <Form.Control size='sm' value={ name } type="text" onChange={ (e) => setName(e.target.value) } />
                            </div>
                        </Form.Group>

                        <Form.Group className='mb-3 row align-items-center'>
                            <Form.Label className='form-label fs-sm col-form-label col-md-3' htmlFor="workflow-type">
                                Workflow Type
                            </Form.Label>
                            <div className="col-md-9 d-flex align-items-center">
                                <Form.Select size='sm' value={ workflowType } onChange={ (e) => setWorkflowType(e.target.value) }>
                                    <option value="9">Non-Sensor</option>
                                    <option value="10">Sensor</option>
                                </Form.Select>
                            </div>
                        </Form.Group>

                        <Form.Group className='mb-3 row align-items-center'>
                            <Form.Label className='form-label fs-sm col-form-label col-md-3' htmlFor="workflow-hue">
                                Hue
                            </Form.Label>
                            <div className="col-md-9 d-flex align-items-center">
                                <ReactSlider className='range-slider range-slider-single' thumbClassName='range-slider-handle' trackClassName='range-slider-track' min={ 0 } max={ 360 } value={ hue } ariaLabel={ ['Handle'] } ariaValuetext={ state => `Handle value ${ state.valueNow }` } step={ 1 } onChange={ (e) => setHue(e) } renderThumb={ (props, state) => (
                                    <div { ...props }>
                                        <div className='range-slider-tooltip'>{ state.valueNow }</div>
                                    </div>
                                ) }
                                />
                                <div style={ { backgroundColor: `hsl(${ hue }, 100%, 50%)`, width: "200px", height: "40px", marginLeft: "1rem", borderRadius: "0.30rem" } } />
                            </div>
                        </Form.Group>

                        <Form.Group className='mb-3 row'>
                            <Form.Label className='form-label fs-sm col-form-label col-md-3' htmlFor="workflow-rgbcolor">
                                RGB Color
                            </Form.Label>
                            <div className="col-md-9 d-flex align-items-center">
                                <div className='color-picker'>
                                    <Form.Control id="workflow-color-picker" className={`color-display ${rgbColor ? "visible" : "invisible"}`} value={ rgbColor ?? "#000000" } type="color" onChange={ (e) => setRgbColor(e.target.value) } />
                                    <div onClick={ () => {
                                        const picker = document.querySelector("#workflow-color-picker");
                                        picker.showPicker()
                                    } } className={`color-display null-display ${rgbColor ? "invisible" : "visible"}`}>
                                    </div>
                                </div>
                                <div className='d-flex gap-2 align-items-center'>
                                    <p className="m-0">{ rgbColor ?? "None" }</p>
                                    { rgbColor && <button className='btn btn-link fi-x m-0 p-1' onClick={() => setRgbColor(null)}></button> }
                                </div>
                            </div>
                        </Form.Group>

                        <Form.Group className='mb-3 row align-items-center'>
                            <Form.Label className='form-label fs-sm col-form-label col-md-3' htmlFor="workflow-order">
                                Order
                            </Form.Label>
                            <div className="col-md-9 d-flex align-items-center">
                                <Form.Control size='sm' value={ order } type="text" onChange={ (e) => setOrder(e.target.value) } />
                            </div>
                        </Form.Group>

                        <hr />
                        <div className='mt-4'>
                            <div className='d-flex align-items-center'>
                                <legend>Configuration</legend>
                                <div className='file-input d-flex align-items-center'>
                                    <input className="file-upload-button" ref={inputRef} onChange={e => handleChange(e)} type="file" id="json-fileUpload" name="kml-file" accept='.json' />
                                    <div className="d-flex align-items-center">
                                        <label for="json-fileUpload"><i className='fi-cloud-upload me-2'></i>Import</label>
                                    </div>
                                </div>

                                <Button variant='outline-primary' onClick={ () => handleDownload() } size='sm'>
                                    <span><i className='fi-download me-2'></i>Export</span>
                                </Button>
                            </div>
                            <DndContext sensors={sensors} onDragStart={event => {
                                setActiveItems(event.active.data.current.sortable.items)
                                setActiveContainerId(event.active.data.current.sortable.containerId)
                                setActiveId(event.active.id)
                            }} onDragEnd={event => handleDragEnd(event)} collisionDetection={closestCorners}>
                                <Form.Group className='mb-3 row'>
                                    <Form.Label className='form-label fs-sm col-form-label col-md-3 pt-4'>
                                        Manual Types
                                    </Form.Label>
                                    <div className="col-md-9 px-0">
                                        <WorkflowConfigTypesTable
                                            workflowHasData={false}
                                            containerId={typesContainerId}
                                            configType={"types"}
                                            setConfigTypes={setConfigTypes}
                                            sortingArray={configTypes}
                                            handleAddClick={() => {setConfigType("types"); setTypeFormData({"geometryType": enums.GeometryType[0]})}}
                                            onEditClick={(item, index) => {setTypeFormData(item); setIsEditingTypeIndex(index); setConfigType("types")}} />
                                    </div>
                                </Form.Group>
                                <Form.Group className='mb-3 row'>
                                    <Form.Label className='form-label fs-sm col-form-label col-md-3 pt-4'>
                                        Sensor Types
                                    </Form.Label>
                                    <div className="col-md-9 px-0">
                                        <WorkflowConfigTypesTable
                                            workflowHasData={false}
                                            containerId={typesTwoContainerId}
                                            configType={"types2"}
                                            setConfigTypes={setConfigTypesTwo}
                                            sortingArray={configTypesTwo}
                                            handleAddClick={() => {setConfigType("types2"); setTypeFormData({"geometryType": enums.GeometryType[0]})}}
                                            onEditClick={(item, index) => {setTypeFormData(item); setIsEditingTypeIndex(index); setConfigType("types2")}} />
                                    </div>
                                </Form.Group>
                                <DragOverlay>
                                    {
                                        activeId && !arrayUtilities.isNullOrEmpty(activeItems)
                                            ? <ConfigTableRowDragOverlay item={ activeItems.find((_, index) => index === Number(activeId.split("-")[1])) } />
                                            : null
                                    }
                                </DragOverlay>
                            </DndContext>
                        </div>
                        <Form.Group className='mt-4 row justify-content-end'>
                        <div className="col-md-9">
                            <Button onClick={ () => navigate(`/${ organisationId }/projects/${projectId}/workflows`) } variant='outline-primary' size="sm">
                                Cancel
                            </Button>
                            <Button disabled={ !canSave } onClick={ updateTemplate } variant='primary' size='sm' className='ms-2'>
                                    Save template
                            </Button>
                        </div>
                    </Form.Group>
                    </div>
                </Col>
            </Row>

            <WorkflowConfigBuilder
                configType={configType}
                setConfigType={setConfigType}
                isEditingTypeIndex={isEditingTypeIndex}
                setIsEditingTypeIndex={setIsEditingTypeIndex}
                saveConfig={saveConfig}
                typeFormData={typeFormData}
                setTypeFormData={setTypeFormData}
                icons={icons}
                isLoadingIcons={isLoadingIcons}
                workflowHasData={false}
            />
            {!stringUtilities.isNullOrEmpty(errorMessage) && (
                <ErrorModal onDismiss={() => setErrorMessage(null)} error={{ message: errorMessage }} />
            )}
        </Container>
    )
}